export type SpeechRecognizerResult = {
  finalText?: string;
  interimText?: string;
};

export type SpeechRecognizerLike = {
  start: () => void;
  stop: () => void;
  destroy: () => void;

  // 新增：外部主动读取
  getText: () => string;
  consumeFinalText: (options?: { includeInterim?: boolean }) => string;

  onresult?: (event: SpeechRecognizerResult) => void;
  onend?: () => void;
  onerror?: (err: any) => void;
};

type CreateBrowserSpeechRecognizerOptions = {
  lang?: string;
  silenceMs?: number; // 这里先保留，不再用于自动发送，只做兼容
};

export function createBrowserSpeechRecognizer(
  options: CreateBrowserSpeechRecognizerOptions = {},
): SpeechRecognizerLike {
  const { lang = 'zh-CN' } = options;

  const SR =
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition;

  if (!SR) {
    throw new Error('SpeechRecognition is not supported in this browser');
  }

  let rec: any = null;
  let destroyed = false;

  // 只做缓冲，不自动提交
  let finalBuffer = '';
  let interimBuffer = '';

  const joinText = (a: string, b: string) => {
    const aa = (a || '').trim();
    const bb = (b || '').trim();
    if (!aa) return bb;
    if (!bb) return aa;
    return `${aa} ${bb}`.trim();
  };

  const resetBuffers = () => {
    finalBuffer = '';
    interimBuffer = '';
  };

  const getMergedPreviewText = () => {
    return joinText(finalBuffer, interimBuffer).trim();
  };

  const emitInterim = () => {
    const text = getMergedPreviewText();
    if (!text) return;

    recognizer.onresult?.({
      finalText: undefined,
      interimText: text,
    });
  };

  const recognizer: SpeechRecognizerLike = {
    onresult: undefined,
    onend: undefined,
    onerror: undefined,

    getText() {
      return getMergedPreviewText();
    },

    consumeFinalText(options = {}) {
      const { includeInterim = true } = options;

      // 默认把当前还没 final 的尾巴也一起带出去
      const text = includeInterim
        ? joinText(finalBuffer, interimBuffer).trim()
        : finalBuffer.trim();

      resetBuffers();
      return text;
    },

    start() {
      if (destroyed) return;
      if (rec) return;

      // 注意：这里不 resetBuffers()
      // 因为你希望断句后还能接着累计

      rec = new SR();
      rec.lang = lang;
      rec.continuous = false;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      rec.onresult = (e: any) => {
        if (destroyed) return;

        let roundFinal = '';
        let roundInterim = '';

        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const r = e.results[i];
          const t = (r?.[0]?.transcript || '').trim();
          if (!t) continue;

          if (r.isFinal) {
            roundFinal = joinText(roundFinal, t);
          } else {
            roundInterim = joinText(roundInterim, t);
          }
        }

        // 浏览器确认过的片段，累加到 finalBuffer
        if (roundFinal) {
          finalBuffer = joinText(finalBuffer, roundFinal);
        }

        // 当前这轮临时结果，只做预览
        interimBuffer = roundInterim;

        emitInterim();
      };

      rec.onerror = (err: any) => {
        recognizer.onerror?.(err);
      };

      rec.onend = () => {
        // 关键：
        // 不在这里自动 flush / 自动提交
        // 只是告诉外部“这一轮浏览器识别结束了”
        // buffer 继续保留，由外部决定什么时候 consumeFinalText()
        rec = null;
        recognizer.onend?.();
      };

      rec.start();
    },

    stop() {
      try {
        rec?.stop?.();
      } catch {}
    },

    destroy() {
      destroyed = true;
      try {
        rec?.stop?.();
      } catch {}
      rec = null;
      resetBuffers();
    },
  };

  return recognizer;
}
